import { inject } from "mobx-react";
import { FacebookShareButton, TwitterShareButton } from "react-share";
import type { SharePlatform } from "@inferno/renderer-shared-core";
import { ClickSection } from "@inferno/renderer-shared-core";
import { PrintButton } from "../../../core/components/integrations/PrintButton.component";
import { FacebookIcon, XIcon } from "@inferno/renderer-shared-ui";
import type { Store } from "@inferno/renderer-shared-core";
import { CloseButton, MagicLink, Modal, Overlay } from "../../../../app/core/ui";
import { CopyLinkButton } from "../../../core/components/integrations/CopyLinkButton.component";
import styles from "./CoastShowShareButtons.module.scss";
import { useState } from "react";
import { overflowUnset } from "@inferno/renderer-shared-core";

interface CoastShowShareButtonsProps {
  url?: string;
  title?: string;
  desc?: string;
  store?: Store;
  thumb?: string;
  hostName: string;
  hostUrl: string;
  callback?: () => void;
}

export const CoastShowShareButtons = inject("store")(
  ({ title, url = "", store, thumb, hostName, hostUrl, callback }: CoastShowShareButtonsProps) => {
    const iconSize = 40;
    const [showModal, setShowModal] = useState(true);
    const clickHandler = (platform: SharePlatform, sectionName = "") => {
      if (store) {
        store.onShareAction.dispatch({
          share: {
            platform,
          },
          sectionName,
          pageName: store.page.currentPage
            ? `${store.microsite ? "microsite_" : ""}${store.page.currentPage.name}`
            : "",
          context: "",
          action: "share",
          url,
        });
      }
    };
    if (!showModal) return null;

    const handleDismiss = () => {
      setShowModal(false);
      overflowUnset();
      callback?.();
    };
    const thumburl = `${thumb}?ops=gravity("center"),fit(56,56),quality(80)`;

    return (
      <>
        <Overlay open={showModal} handleClick={handleDismiss} />
        <div className={styles.modalContainer}>
          <Modal open={showModal} callback={handleDismiss} addClass={styles.coastShareIcons}>
            <div className={styles.modalContent}>
              <div className={styles.titleBlock}>
                <span className={styles.title}>Share</span>
                <CloseButton addClass="absoulte" height={24} width={24} label="close the modal" click={handleDismiss} />
              </div>
              <figure className={styles.showDetailBlockWrapper}>
                <section className={styles.thumb}>
                  <MagicLink to={url}>
                    <img height="56" width="56" src={thumburl} alt={"coast show image"} />
                  </MagicLink>
                </section>
                <figcaption>
                  <div className={styles.showDetailTitle}>
                    <MagicLink to={url} target="_blank">
                      <p className={styles.showName}>{title}</p>
                    </MagicLink>
                    <MagicLink to={hostUrl} target="_blank">
                      <p className={styles.hostName}>{hostName}</p>
                    </MagicLink>
                  </div>
                </figcaption>
              </figure>
              <div className={styles.socialIconsBlock}>
                <ClickSection.Consumer>
                  {sectionName => (
                    <div className={styles.iconAndText}>
                      <CopyLinkButton callback={() => clickHandler("copyLink", sectionName)}>
                        {<span className={styles.text}>Copy link</span>}
                      </CopyLinkButton>
                    </div>
                  )}
                </ClickSection.Consumer>
                <ClickSection.Consumer>
                  {sectionName => (
                    <div className={styles.iconAndText}>
                      <FacebookShareButton
                        data-platform={"facebook"}
                        // ={desc}
                        hashtag={`#${title}`}
                        url={url}
                        aria-label="Share this page on Facebook"
                        onClick={() => clickHandler("facebook", sectionName)}
                      >
                        <FacebookIcon />
                        {<span className={styles.text}>Share on Facebook</span>}
                      </FacebookShareButton>
                    </div>
                  )}
                </ClickSection.Consumer>
                <ClickSection.Consumer>
                  {sectionName => (
                    <div className={styles.iconAndText}>
                      <TwitterShareButton
                        data-platform={"x"}
                        title={title}
                        url={url}
                        aria-label="Share this page on X"
                        onClick={() => clickHandler("x", sectionName)}
                      >
                        <XIcon />
                        {<span className={styles.text}>Share on X</span>}
                      </TwitterShareButton>
                    </div>
                  )}
                </ClickSection.Consumer>
                <ClickSection.Consumer>
                  {sectionName => (
                    <div className={styles.iconAndText}>
                      <PrintButton
                        width={iconSize}
                        height={iconSize}
                        callback={async () => {
                          handleDismiss();
                          clickHandler("print", sectionName);
                        }}
                      >
                        {<span className={styles.text}>Print</span>}
                      </PrintButton>
                    </div>
                  )}
                </ClickSection.Consumer>
              </div>
            </div>
          </Modal>
        </div>
      </>
    );
  },
);
